/**
 * Global chat context — controls the floating chat window state.
 * Rendered at App level so the window persists across page navigation.
 */
import { createContext, useContext, useState, useCallback, type ReactNode } from "react";

export interface FloatingChatState {
  isOpen: boolean;
  userId: string | null;
  userName: string | null;
  position: { x: number; y: number };
  /** When set, shows a "Back to conversation" banner on any page */
  returnThread: { userId: string; userName: string } | null;
}

interface ChatContextValue {
  floatingChat: FloatingChatState;
  openFloat: (userId: string, userName: string) => void;
  closeFloat: () => void;
  setFloatPosition: (pos: { x: number; y: number }) => void;
  setReturnThread: (thread: { userId: string; userName: string } | null) => void;
}

const ChatContext = createContext<ChatContextValue | null>(null);

const DEFAULT_STATE: FloatingChatState = {
  isOpen: false,
  userId: null,
  userName: null,
  position: { x: window.innerWidth - 400, y: window.innerHeight - 540 },
  returnThread: null,
};

export function ChatProvider({ children }: { children: ReactNode }) {
  const [floatingChat, setFloatingChat] = useState<FloatingChatState>(() => ({
    ...DEFAULT_STATE,
    position: { x: Math.max(0, window.innerWidth - 400), y: Math.max(0, window.innerHeight - 540) },
  }));

  const openFloat = useCallback((userId: string, userName: string) => {
    setFloatingChat((prev) => ({
      ...prev,
      isOpen: true,
      userId,
      userName,
    }));
  }, []);

  const closeFloat = useCallback(() => {
    setFloatingChat((prev) => ({ ...prev, isOpen: false }));
  }, []);

  const setFloatPosition = useCallback((pos: { x: number; y: number }) => {
    setFloatingChat((prev) => ({ ...prev, position: pos }));
  }, []);

  const setReturnThread = useCallback((thread: { userId: string; userName: string } | null) => {
    setFloatingChat((prev) => ({ ...prev, returnThread: thread }));
  }, []);

  return (
    <ChatContext.Provider value={{ floatingChat, openFloat, closeFloat, setFloatPosition, setReturnThread }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChatContext() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error("useChatContext must be used inside ChatProvider");
  return ctx;
}
